import { StyleSheet } from 'aphrodite';

import {
	fadeOutZoomOut,
	slideInFromBottomNFadeNZoom,
	slideInFromLeftNFadeNZoom,
	slideInFromRightNFadeNZoom,
	slideInFromTopNFadeNZoom,
} from '../../styles/animations';
import {
	borderColor,
	borderOutline,
	brandPrimaryHover,
	charmGray,
	darkGrayFontColor,
	white,
} from '../../styles/colors';

import { shadowMd } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	dropdownPopup: {
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.main,
		transformOrigin: 'var(--transform-origin)',
		boxSizing: 'border-box',
		boxShadow: `0 0 0 1px rgb(0 0 0 / 0.1), ${shadowMd}`,
		padding: 4,
		borderRadius: THEME.common.borderRadius,
		animationDuration: '100ms',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		animationFillMode: 'forwards',
		':is([data-closed])': {
			animationName: fadeOutZoomOut,
			overflow: 'hidden',
		},
		':is([data-open][data-side="bottom"])': {
			animationName: slideInFromTopNFadeNZoom(),
		},
		':is([data-open][data-side="top"])': {
			animationName: slideInFromBottomNFadeNZoom(),
		},
		':is([data-open][data-side="left"])': {
			animationName: slideInFromRightNFadeNZoom(),
		},
		':is([data-open][data-side="right"])': {
			animationName: slideInFromLeftNFadeNZoom(),
		},
		outline: 'none',
		position: 'relative',
		minWidth: 128,
		width: `var(--anchor-width)`,
		maxHeight: `var(--available-height)`,
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	dropdownPositioner: {
		zIndex: 50,
		outline: 'none',
	},
	menuItem: {
		display: 'flex',
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: THEME.colors.text.dark,
		cursor: 'pointer',
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: `#f4f5f7`,
		},
		':has(*) svg': {
			pointerEvents: 'none',
			flexShrink: 0,
			width: 12,
			height: 12,
		},
	},
	separator: {
		margin: '4px -4px',
		height: 1,
		backgroundColor: borderOutline,
	},
	groupLabel: {
		padding: `6px 8px`,
		fontSize: '0.75rem',
		color: darkGrayFontColor,
	},
	checkboxItem: {
		display: `flex`,
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: THEME.colors.text.dark,
		cursor: 'pointer',
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: `#f4f5f7`,
		},
	},
	checkBoxItemCheckWrapper: {
		border: `1px solid ${charmGray}`,
		width: 12,
		height: 12,
		flexShrink: 0,
		borderRadius: 4,
		display: `flex`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkBoxItemCheckIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkBoxItemCheck: {
		width: 8,
		height: 8,
	},
	radioItem: {
		display: `flex`,
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: darkGrayFontColor,
		cursor: 'pointer',
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: `#f4f5f7`,
		},
	},
	radioItemCheckWrapper: {
		border: `1px solid ${charmGray}`,
		width: 12,
		height: 12,
		flexShrink: 0,
		borderRadius: '50%',
		display: `flex`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioItemCheckIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioItemCheck: {
		width: 8,
		height: 8,
	},
	subTrigger: {
		display: 'flex',
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: darkGrayFontColor,
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: brandPrimaryHover,
			color: white,
		},
		':has(*) svg': {
			pointerEvents: 'none',
			flexShrink: 0,
			width: 12,
			height: 12,
		},
	},
	subTriggerChevron: {
		marginLeft: 'auto',
		width: 12,
		height: 12,
	},
	selectTrigger: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: 36,
		width: 'fit-content',
		whiteSpace: 'nowrap',
		borderRadius: 6,
		border: `1px solid ${borderColor}`,
		backgroundColor: 'transparent',
		padding: `8px 12px`,
		fontSize: '0.875rem',
		lineHeight: '1.25rem',
		boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
		color: THEME.colors.text.dark,
		':focus': {
			outline: 'none',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
		':has(*) > span': {
			overflow: 'hidden',
			display: '-webkit-box',
			'-webkit-box-orient': 'vertical',
			'-webkit-line-clamp': 1,
		},
	},
	ellipsisTrigger: {
		boxSizing: 'border-box',
		width: 16,
		height: 16,
		':has(*) > svg': {
			width: '100%',
			height: '100%',
		},
	},
	triggerChevron: {
		display: 'block',
		boxSizing: 'border-box',
		height: 16,
		width: 16,
		opacity: 0.5,
		flexShrink: 0,
	},
});
